import { createAction, handleActions } from 'redux-actions';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import _ from 'lodash';
import { OrgHistoryModel } from '../../../../models/org-history.model';
import { showError } from '@weave/alert-system';
import { REACT_APP_API_URL } from '../../../../config/app.js';

export interface OrgHistoryState {
  history: OrgHistoryModel[];
  loading: boolean;
}

export const getOrgHistory = createAction('GET_ORG_HISTORY');
export const getOrgHistorySuccess = createAction<OrgHistoryModel[]>(
  'GET_ORG_HISTORY_SUCCESS'
);
export const getOrgHistoryFailure = createAction<Error>('GET_ORG_HISTORY_FAILURE');
export const addOrgToHistory = createAction<OrgHistoryModel>('ADD_ORG_TO_HISTORY');
export const removeOrgFromHistory = createAction<OrgHistoryModel>(
  'REMOVE_ORG_FROM_HISTORY'
);

const isProd = REACT_APP_API_URL.startsWith('https://api');
const orgHistoryKey = isProd ? 'org-history' : 'dev-org-history';

// Cap the number of entries persisted to localStorage so the history list can't grow
// indefinitely and eventually crash the app with a QuotaExceededError.
const MAX_HISTORY_ENTRIES = 50;

const getHistoryFromLocalStorage = (): OrgHistoryModel[] => {
  const history = window.localStorage.getItem(orgHistoryKey);
  return history ? JSON.parse(history) : [];
};

const saveHistoryToLocalStorage = (historyList: OrgHistoryModel[]) => {
  const cappedHistoryList = _.sortBy(historyList, (org) => -org.created).slice(
    0,
    MAX_HISTORY_ENTRIES
  );
  window.localStorage.setItem(orgHistoryKey, JSON.stringify(cappedHistoryList));
};

export const handleGetOrgHistory = function* () {
  try {
    const historyList: OrgHistoryModel[] = yield call(getHistoryFromLocalStorage);
    yield put(getOrgHistorySuccess(historyList));
  } catch (error) {
    yield put(getOrgHistoryFailure(error as Error));
    yield put(showError('Error attempting to read org history list'));
  }
};

export const handleAddOrg = function* (action) {
  try {
    const org: OrgHistoryModel = action.payload;
    const historyList: OrgHistoryModel[] = yield call(getHistoryFromLocalStorage);

    const existingOrg = _.find(historyList, { id: org.id });
    if (existingOrg) {
      existingOrg.created = Date.now();
    } else {
      org.created = Date.now();
      historyList.push(org);
    }

    saveHistoryToLocalStorage(historyList);
    yield put(getOrgHistorySuccess(historyList));
  } catch {
    yield put(showError('Error adding org to history list'));
  }
};

export const handleRemoveOrg = function* (action) {
  try {
    const org: OrgHistoryModel = action.payload;
    const historyList: OrgHistoryModel[] = yield call(getHistoryFromLocalStorage);
    _.remove(historyList, { id: org.id });

    saveHistoryToLocalStorage(historyList);
    yield put(getOrgHistorySuccess(historyList));
  } catch {
    yield put(showError('Error removing org from history list'));
  }
};

export const orgHistorySaga = function* () {
  yield all([
    takeEvery(getOrgHistory.toString(), handleGetOrgHistory),
    takeEvery(addOrgToHistory.toString(), handleAddOrg),
    takeEvery(removeOrgFromHistory.toString(), handleRemoveOrg),
  ]);
};

const defaultState: OrgHistoryState = {
  history: [],
  loading: true,
};

export const orgHistoryReducer = handleActions<OrgHistoryState, any>(
  {
    [getOrgHistory.toString()]: (state) => Object.assign({}, state, { loading: true }),
    [getOrgHistorySuccess.toString()]: (state, action) =>
      Object.assign({}, state, {
        history:
          typeof action.payload !== 'undefined'
            ? [...action.payload].sort((a, b) => b.created - a.created)
            : state.history,
        loading: false,
      }),
    [getOrgHistoryFailure.toString()]: (state, action) =>
      Object.assign({}, state, {
        error: action.payload,
        loading: false,
      }),
  },
  defaultState
);
